// Mise en page de la zone de jeu : le viseur (carré) prend la plus grande taille possible entre les blocs fixes (barre du
// haut, nom de l'appli, pellicule, boutons), sans dépasser la largeur ni la hauteur disponibles de la zone de jeu.
// L'espace qui reste est réparti à parts égales entre tous les blocs par le flex de #gameArea (space-between) :
// même espace entre le nom et la pellicule, la pellicule et le viseur, le viseur et les boutons.
// Les blocs facultatifs (le niveau, sous le nom) s'effacent quand, avec eux, le viseur ne prendrait plus toute la largeur.

/**
 * @param {{ area: HTMLElement, fixed: HTMLElement[], viewfinder: HTMLElement, sameWidth?: HTMLElement[], optional?: HTMLElement[],
 *   canHide?: () => boolean, onChange?: () => void }} options
 *   fixed : les blocs de hauteur propre, au-dessus et au-dessous du viseur
 *   sameWidth : les blocs qui prennent exactement la largeur du viseur (la pellicule va jusqu'aux mêmes bords que lui)
 *   optional : des éléments à l'intérieur des blocs fixes, masqués (attribut hidden) quand le viseur manque de place
 *   canHide : faux quand ils doivent rester affichés quoi qu'il arrive (en paysage)
 */
export function createLayout({ area, fixed, viewfinder, sameWidth = [], optional = [], canHide = () => true, onChange }) {
  // Hauteur prise par chaque élément facultatif (marge du haut comprise), mesurée tant qu'il est affiché : une fois masqué, il ne mesure plus rien
  const optionalSpace = new Map();
  const spaceOf = (el) => el.offsetHeight + (parseFloat(getComputedStyle(el).marginTop) || 0);

  function fit() {
    for (const el of optional) if (!el.hidden) optionalSpace.set(el, spaceOf(el));
    const style = getComputedStyle(area);
    const gap = parseFloat(style.rowGap) || 0;
    const padding = (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0);
    const fixedHeight = fixed.reduce((sum, el) => sum + el.offsetHeight, 0);
    // Un espace minimal entre chaque bloc affiché (les blocs masqués, par exemple en paysage, n'en comptent pas)
    const gaps = fixed.filter((el) => el.offsetHeight > 0).length * gap;
    let availableH = area.clientHeight - padding - fixedHeight - gaps;
    // Avec les éléments facultatifs affichés, le viseur prendrait-il toute la largeur ? Sinon on les masque et il récupère leur place.
    // La décision ne dépend que des hauteurs mesurées : la refaire après le changement (le bloc qui les contient change de taille) donne la même.
    const optionalH = optional.reduce((sum, el) => sum + (optionalSpace.get(el) || 0), 0);
    const shownH = optional.reduce((sum, el) => sum + (el.hidden ? 0 : optionalSpace.get(el) || 0), 0);
    const hide = canHide() && availableH + shownH - optionalH < area.clientWidth;
    for (const el of optional) {
      if (el.hidden === hide) continue;
      el.hidden = hide;
      availableH += hide ? optionalSpace.get(el) || 0 : -(optionalSpace.get(el) || 0);
    }
    const size = Math.max(120, Math.floor(Math.min(area.clientWidth, availableH)));
    const px = `${size}px`;
    for (const el of sameWidth) el.style.width = px;
    if (viewfinder.style.width !== px) {
      viewfinder.style.width = px;
      onChange?.();
    }
  }

  const observer = new ResizeObserver(fit);
  observer.observe(area);
  for (const el of fixed) observer.observe(el);
  fit();

  return { fit, destroy: () => observer.disconnect() };
}
